import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  FormControlLabel,
  Switch,
  Typography,
  Grid,
  MenuItem,
  Alert,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import userManagementService from '../../services/userManagementService';
import roleService from '../../services/roleService';
import storeService from '../../services/storeService';
import { useStore } from '../../context/StoreContext';

const UserForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { t } = useTranslation();
  const isEdit = Boolean(id);
  const { currentStore, isSuperAdmin } = useStore();

  const [formData, setFormData] = useState({
    email: '',
    name: '',
    password: '',
    globalRole: 'STORE_USER',
    storeId: currentStore?.id || '',
    roleId: '',
    isActive: true
  });

  const [availableRoles, setAvailableRoles] = useState([]);
  const [availableStores, setAvailableStores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (isSuperAdmin) fetchStores();
    if (isEdit) {
      fetchUser();
    } else {
      if (currentStore) {
        setFormData(prev => ({ ...prev, storeId: currentStore.id }));
        fetchRolesForStore(currentStore.id);
      }
    }
  }, [id, currentStore]);

  useEffect(() => {
    if (formData.storeId && formData.globalRole === 'STORE_USER') {
      fetchRolesForStore(formData.storeId);
    } else {
      setAvailableRoles([]);
    }
  }, [formData.storeId, formData.globalRole]);

  const fetchStores = async () => {
    try {
      const data = await storeService.getAllStores();
      setAvailableStores(data);
    } catch (error) {
      console.error('Failed to fetch stores:', error);
    }
  };

  const fetchRolesForStore = async (storeId) => {
    try {
      const previousStoreId = localStorage.getItem('currentStoreId');
      localStorage.setItem('currentStoreId', storeId);
      const data = await roleService.getActiveRoles();
      setAvailableRoles(data);
      if (previousStoreId) {
        localStorage.setItem('currentStoreId', previousStoreId);
      } else {
        localStorage.removeItem('currentStoreId');
      }
    } catch (error) {
      console.error('Failed to fetch roles:', error);
      toast.error(t('common.loadError'));
    }
  };

  const fetchUser = async () => {
    try {
      const data = await userManagementService.getUserById(id);
      setFormData({
        email: data.email,
        name: data.name,
        password: '',
        globalRole: data.globalRole,
        storeId: data.storeId || '',
        roleId: data.roleId || '',
        isActive: data.isActive
      });
      if (data.storeId) fetchRolesForStore(data.storeId);
    } catch (error) {
      toast.error(t('common.loadError'));
      console.error(error);
    }
  };

  const handleStoreChange = (newStoreId) => {
    setFormData(prev => ({ ...prev, storeId: newStoreId, roleId: '' }));
    if (newStoreId) {
      fetchRolesForStore(newStoreId);
    } else {
      setAvailableRoles([]);
    }
  };

  const handleGlobalRoleChange = (newGlobalRole) => {
    setFormData(prev => ({ ...prev, globalRole: newGlobalRole, roleId: '' }));
    if (newGlobalRole === 'SUPER_ADMIN') setAvailableRoles([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (formData.globalRole === 'STORE_USER' && !formData.storeId) {
      toast.error(t('users.assignedStore') + ' ' + t('common.none'));
      setLoading(false);
      return;
    }

    try {
      if (isEdit) {
        await userManagementService.updateUser(id, formData);
        toast.success(t('users.updateSuccess'));
      } else {
        await userManagementService.createUser(formData);
        toast.success(t('users.createSuccess'));
      }
      navigate('/users');
    } catch (error) {
      toast.error(error.response?.data?.message || t('common.saveError'));
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3 }}>
        {isEdit ? t('users.editUser') : t('users.createUser')}
      </Typography>

      {!currentStore && !isSuperAdmin && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {t('users.assignedStore')}
        </Alert>
      )}

      <Card>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('common.email')}
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  disabled={isEdit}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('common.name')}
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t('common.password')}
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required={!isEdit}
                  helperText={isEdit ? t('users.changePassword') : ''}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword((prev) => !prev)}
                          edge="end"
                          size="small"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  label={t('users.globalRole')}
                  value={formData.globalRole}
                  onChange={(e) => handleGlobalRoleChange(e.target.value)}
                  required
                  disabled={!isSuperAdmin}
                >
                  <MenuItem value="SUPER_ADMIN">Super Admin</MenuItem>
                  <MenuItem value="STORE_USER">Store User</MenuItem>
                </TextField>
              </Grid>

              {formData.globalRole === 'STORE_USER' && (
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    select
                    label={t('users.assignedStore')}
                    value={formData.storeId}
                    onChange={(e) => handleStoreChange(e.target.value)}
                    required
                    disabled={!isSuperAdmin && isEdit}
                  >
                    {isSuperAdmin ? (
                      availableStores.map((store) => (
                        <MenuItem key={store.id} value={store.id}>
                          {store.name} ({store.code})
                        </MenuItem>
                      ))
                    ) : (
                      currentStore && (
                        <MenuItem value={currentStore.id}>
                          {currentStore.name} ({currentStore.code})
                        </MenuItem>
                      )
                    )}
                  </TextField>
                </Grid>
              )}

              {formData.globalRole === 'STORE_USER' && formData.storeId && (
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    select
                    label={t('users.role')}
                    value={formData.roleId}
                    onChange={(e) => setFormData({ ...formData, roleId: e.target.value })}
                  >
                    <MenuItem value="">
                      <em>{t('common.none')}</em>
                    </MenuItem>
                    {availableRoles.map((role) => (
                      <MenuItem key={role.id} value={role.id}>
                        {role.displayName} ({role.name})
                      </MenuItem>
                    ))}
                  </TextField>
                  {availableRoles.length === 0 && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      {t('users_ext.noRolesAvailable')}
                    </Typography>
                  )}
                </Grid>
              )}

              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    />
                  }
                  label={t('common.active')}
                />
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <Button onClick={() => navigate('/users')} disabled={loading}>
                    {t('common.cancel')}
                  </Button>
                  <Button type="submit" variant="contained" disabled={loading}>
                    {loading ? t('common.saving') : isEdit ? t('common.update') : t('common.create')}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default UserForm;
